<template>
  <header class="site-header" :class="{ 'is-scrolled': scrolled }">

    <!-- Top announcement bar -->
    <div class="announce-bar">
      <span>Free shipping on orders over $75 &nbsp;·&nbsp; Use code <strong>NEXUS10</strong> for 10% off</span>
    </div>

    <div class="header-shell">

      <!-- ── Logo ─────────────────────────────────────────────────────── -->
      <router-link to="/" class="logo" aria-label="Nexus home">
        <svg class="logo-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
          <rect x="2"  y="2"  width="12" height="12" rx="3" fill="currentColor"/>
          <rect x="18" y="2"  width="12" height="12" rx="3" fill="currentColor" opacity=".45"/>
          <rect x="2"  y="18" width="12" height="12" rx="3" fill="currentColor" opacity=".45"/>
          <rect x="18" y="18" width="12" height="12" rx="3" fill="currentColor"/>
        </svg>
        <span class="logo-wordmark">NEXUS</span>
      </router-link>

      <!-- ── Primary nav ────────────────────────────────────────────── -->
      <nav class="primary-nav" aria-label="Site navigation">
        <ul>
          <li v-for="item in navItems" :key="item.label">
            <router-link :to="item.path" class="nav-link">
              {{ item.label }}
              <span class="ink-bar" />
            </router-link>
          </li>
        </ul>
      </nav>

      <!-- ── Right cluster ─────────────────────────────────────────── -->
      <div class="action-cluster">

        <!-- Search -->
        <div class="search-pill" :class="{ expanded: searchExpanded }">
          <transition name="fade-slide">
            <input
              v-if="searchExpanded"
              ref="searchEl"
              v-model="searchQ"
              class="search-field"
              type="text"
              placeholder="Search…"
              @keydown.enter="submitSearch"
              @keydown.escape="collapseSearch"
              aria-label="Search"
            />
          </transition>
          <button
                v-if="searchExpanded && searchQ.trim()"
                class="search-submit-btn"
                @click="submitSearch"
                aria-label="Submit search"
            >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
                <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
            </button>
          <button class="act-btn" @click="toggleSearch" :aria-label="searchExpanded ? 'Close search' : 'Open search'">
            <svg v-if="!searchExpanded" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
            <svg v-else width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
          </button>
        </div>

        <!-- Auth -->
        <div class="auth-node" v-click-outside="closeAuth">
          <button class="act-btn" @click="authOpen = !authOpen" aria-label="Account">
            <span v-if="loggedIn" class="avatar-chip">{{ initials }}</span>
            <svg v-else width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
          </button>

          <transition name="panel-drop">
            <div v-if="authOpen" class="auth-panel">
              <div class="panel-notch" />

              <!-- Logged-in state -->
              <template v-if="loggedIn">
                <p class="panel-greeting">Hello, <strong>{{ user.name.split(' ')[0] }}</strong></p>
                <ul class="user-links">
                  <li><router-link to="/account" @click="authOpen=false">My Account</router-link></li>
                  <li><router-link to="/orders"  @click="authOpen=false">Orders</router-link></li>
                  <li><router-link to="/wishlist" @click="authOpen=false">Wishlist</router-link></li>
                </ul>
                <button class="panel-signout" @click="logout">Sign Out</button>
              </template>

              <!-- Auth tabs -->
              <template v-else>
                <div class="auth-tabs">
                  <button :class="['auth-tab', { active: tab === 'login' }]"    @click="tab='login'">Sign In</button>
                  <button :class="['auth-tab', { active: tab === 'register' }]" @click="tab='register'">Register</button>
                </div>

                <form class="auth-form" @submit.prevent="tab === 'login' ? submitLogin() : submitRegister()">
                  <template v-if="tab === 'register'">
                    <div class="input-wrap">
                      <input v-model="reg.name" type="text" placeholder="Full name" required autocomplete="name" />
                    </div>
                  </template>
                  <div class="input-wrap">
                    <input v-model="creds.email" type="email" placeholder="Email address" required autocomplete="email" />
                  </div>
                  <div class="input-wrap">
                    <input v-model="creds.password" type="password" placeholder="Password" required autocomplete="current-password" />
                  </div>
                  <a v-if="tab === 'login'" href="#" class="forgot">Forgot password?</a>
                  <button class="panel-submit" :disabled="authBusy">
                    <span v-if="!authBusy">{{ tab === 'login' ? 'Sign In' : 'Create Account' }}</span>
                    <span v-else class="dot-spin" />
                  </button>
                  <p v-if="authErr" class="form-error">{{ authErr }}</p>
                </form>
              </template>
            </div>
          </transition>
        </div>

        <!-- Cart trigger -->
        <button class="act-btn cart-trigger" @click="cart.toggleCart()" aria-label="Shopping cart">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
          <span v-if="cart.getItemCount > 0" class="badge">{{ cart.getItemCount > 9 ? '9+' : cart.getItemCount }}</span>
        </button>

      </div>

      <!-- ── Hamburger (mobile) ─────────────────────────────────────── -->
      <button class="burger" :class="{ open: mobileOpen }" @click="mobileOpen = !mobileOpen" aria-label="Toggle menu">
        <span /><span /><span />
      </button>

    </div>

    <!-- ── Mobile nav drawer ─────────────────────────────────────────── -->
    <transition name="mobile-slide">
      <nav v-if="mobileOpen" class="mobile-drawer" aria-label="Mobile navigation">
        <ul class="mobile-links">
          <li v-for="item in navItems" :key="item.label">
            <router-link :to="item.path" @click="mobileOpen = false">{{ item.label }}</router-link>
          </li>
        </ul>
        <div v-if="!loggedIn" class="mobile-auth-row">
          <router-link to="/login"    class="mob-btn outline" @click="mobileOpen=false">Sign In</router-link>
          <router-link to="/register" class="mob-btn"         @click="mobileOpen=false">Register</router-link>
        </div>
        <div v-else class="mobile-auth-row">
          <span class="mob-user">{{ user.name }}</span>
          <button class="mob-btn outline" @click="logout">Sign Out</button>
        </div>
      </nav>
    </transition>

    <!-- ── Cart drawer ────────────────────────────────────────────────── -->
    <transition name="cart-slide">
      <div v-if="cart.isCartOpen" class="cart-scrim" @click.self="cart.closeCart()">
        <aside class="cart-drawer">

          <!-- ── Header ── -->
          <div class="cd-head">
            <div class="cd-head-left">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
              <div>
                <h2 class="cd-title">Your Cart</h2>
                <p class="cd-subtitle">{{ cart.getItemCount }} item{{ cart.getItemCount !== 1 ? 's' : '' }} · ${{ cart.getSubtotal.toFixed(2) }}</p>
              </div>
            </div>
            <button class="cd-close" @click="cart.closeCart()" aria-label="Close cart">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
            </button>
          </div>

          <!-- ── Free shipping progress ── -->
          <div v-if="!cart.isEmpty" class="cd-shipping-bar">
            <div class="csb-text">
              <span v-if="freeShippingRemaining > 0">
                <strong>${{ freeShippingRemaining.toFixed(2) }}</strong> away from free shipping
              </span>
              <span v-else class="csb-unlocked">
                🎉 You've unlocked free shipping!
              </span>
            </div>
            <div class="csb-track">
              <div class="csb-fill" :style="{ width: freeShippingProgress + '%' }" />
            </div>
          </div>

          <!-- ── Empty state ── -->
          <div v-if="cart.isEmpty" class="cd-empty">
            <div class="cd-empty-icon">
              <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round">
                <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/>
                <path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/>
              </svg>
            </div>
            <p class="cd-empty-title">Your cart is empty</p>
            <p class="cd-empty-sub">Looks like you haven't added anything yet.</p>
            <button class="cd-shop-btn" @click="cart.closeCart()">Start Shopping</button>
          </div>

          <!-- ── Item list ── -->
          <ul v-else class="cd-list">
            <li
              v-for="item in cart.getItems"
              :key="item.id"
              class="cd-item"
              :class="{ 'is-removing': removingId === item.id }"
            >
              <!-- Thumbnail -->
              <div class="cd-thumb">
                <img v-if="item.image" :src="item.image" :alt="item.name" />
                <div v-else class="cd-thumb-placeholder" :style="{ background: thumbGradient(item.id) }">
                  <span class="cd-thumb-initial">{{ item.name[0].toUpperCase() }}</span>
                </div>
                <span v-if="item.sku" class="cd-thumb-badge">{{ item.category || 'Item' }}</span>
              </div>

              <!-- Details -->
              <div class="cd-details">
                <!-- Top row: name + delete -->
                <div class="cd-details-top">
                  <div class="cd-name-group">
                    <p class="cd-item-category">{{ item.category || item.sku || 'Product' }}</p>
                    <p class="cd-item-name">{{ item.name }}</p>
                  </div>
                  <button class="cd-delete" @click="removeItem(item.id)" aria-label="Remove item" title="Remove">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                      <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                    </svg>
                  </button>
                </div>

                <!-- Price row -->
                <div class="cd-price-row">
                  <span class="cd-unit-price">${{ item.price.toFixed(2) }} each</span>
                  <span class="cd-line-total">${{ (item.price * item.quantity).toFixed(2) }}</span>
                </div>

                <!-- Qty controls -->
                <div class="cd-qty-wrap">
                  <div class="cd-qty-controls">
                    <button
                      class="cd-qty-btn"
                      :class="{ 'is-danger': item.quantity === 1 }"
                      @click="item.quantity === 1 ? removeItem(item.id) : cart.decrementItem(item.id)"
                      :aria-label="item.quantity === 1 ? 'Remove item' : 'Decrease quantity'"
                    >
                      <svg v-if="item.quantity === 1" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
                      <svg v-else width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M5 12h14"/></svg>
                    </button>
                    <span class="cd-qty-num">{{ item.quantity }}</span>
                    <button class="cd-qty-btn" @click="cart.incrementItem(item.id)" aria-label="Increase quantity">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg>
                    </button>
                  </div>
                  <span v-if="item.quantity > 1" class="cd-qty-saving">
                    {{ item.quantity }}× in cart
                  </span>
                </div>
              </div>
            </li>
          </ul>

          <!-- ── Footer ── -->
          <div v-if="!cart.isEmpty" class="cd-footer">

            <!-- Coupon -->
            <div class="cd-coupon" :class="{ applied: cart.couponCode }">
              <template v-if="!cart.couponCode">
                <div class="cd-coupon-row">
                  <input
                    v-model="couponInput"
                    class="cd-coupon-input"
                    type="text"
                    placeholder="Coupon code"
                    @keydown.enter="applyCoupon"
                  />
                  <button class="cd-coupon-btn" @click="applyCoupon" :disabled="!couponInput.trim()">Apply</button>
                </div>
                <p v-if="couponError" class="cd-coupon-err">{{ couponError }}</p>
              </template>
              <div v-else class="cd-coupon-applied">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M20 6 9 17l-5-5"/></svg>
                <span>Code <strong>{{ cart.couponCode }}</strong> applied — saving ${{ cart.discount.toFixed(2) }}</span>
                <button class="cd-coupon-remove" @click="cart.removeCoupon()">✕</button>
              </div>
            </div>

            <!-- Order summary -->
            <div class="cd-summary">
              <div class="cd-summary-row">
                <span>Subtotal ({{ cart.getItemCount }} items)</span>
                <span>${{ cart.getSubtotal.toFixed(2) }}</span>
              </div>
              <div class="cd-summary-row">
                <span>Shipping</span>
                <span :class="{ 'free-label': cart.getSubtotal >= 75 }">
                  {{ cart.getSubtotal >= 75 ? 'FREE' : '$9.99' }}
                </span>
              </div>
              <div v-if="cart.discount > 0" class="cd-summary-row discount-row">
                <span>Discount ({{ cart.couponCode }})</span>
                <span>−${{ cart.discount.toFixed(2) }}</span>
              </div>
              <div class="cd-summary-total">
                <span>Total</span>
                <strong>${{ orderTotal.toFixed(2) }}</strong>
              </div>
            </div>

            <!-- Actions -->
            <router-link to="/checkout" class="cd-checkout" @click="cart.closeCart()">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              Checkout · ${{ orderTotal.toFixed(2) }}
            </router-link>

            <div class="cd-secondary-actions">
              <button class="cd-continue" @click="cart.closeCart()">
                ← Continue Shopping
              </button>
              <button class="cd-clear" @click="confirmClear">
                Clear Cart
              </button>
            </div>

            <!-- Security note -->
            <div class="cd-secure">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              Secure checkout · SSL encrypted
            </div>

          </div>
        </aside>
      </div>
    </transition>

  </header>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';

import { useCartStore } from '@/store/cart';

import { useRouter } from 'vue-router';
const router = useRouter();

// ── Stores ───────────────────────────────────────────────────────────────────
const cart = useCartStore();

function submitSearch() {
  if (!searchQ.value.trim()) return;
  router.push({ path: '/products', query: { q: searchQ.value.trim() } });
  collapseSearch();
}

// ── Nav ──────────────────────────────────────────────────────────────────────
const navItems = [
  { label: 'Home',     path: '/'         },
  { label: 'About',    path: '/about'    },
  { label: 'Products', path: '/products' },
  { label: 'Contact',  path: '/contact'  },
];

// ── Scroll ───────────────────────────────────────────────────────────────────
const scrolled = ref(false);
const handleScroll = () => { scrolled.value = window.scrollY > 24; };
onMounted(() => { window.addEventListener('scroll', handleScroll, { passive: true }); cart.init(); });
onUnmounted(() => window.removeEventListener('scroll', handleScroll));

// ── Mobile ───────────────────────────────────────────────────────────────────
const mobileOpen = ref(false);

// ── Search ───────────────────────────────────────────────────────────────────
const searchExpanded = ref(false);
const searchQ  = ref('');
const searchEl = ref(null);

async function toggleSearch() {
  searchExpanded.value = !searchExpanded.value;
  if (searchExpanded.value) { await nextTick(); searchEl.value?.focus(); }
  else searchQ.value = '';
}
function collapseSearch() { searchExpanded.value = false; searchQ.value = ''; }

// ── Auth ─────────────────────────────────────────────────────────────────────
const authOpen = ref(false);
const tab      = ref('login');
const authBusy = ref(false);
const authErr  = ref('');
const loggedIn = ref(false);
const user     = ref({ name: '', email: '' });
const creds    = ref({ email: '', password: '' });
const reg      = ref({ name: '' });

const initials = computed(() =>
  user.value.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
);

function closeAuth() { authOpen.value = false; authErr.value = ''; }

async function submitLogin() {
  authBusy.value = true; authErr.value = '';
  try {
    await new Promise(r => setTimeout(r, 700)); // replace with real API
    user.value   = { name: 'Jane Doe', email: creds.value.email };
    loggedIn.value = true;
    closeAuth();
  } catch { authErr.value = 'Invalid credentials. Please try again.'; }
  finally { authBusy.value = false; }
}

async function submitRegister() {
  authBusy.value = true; authErr.value = '';
  try {
    await new Promise(r => setTimeout(r, 700)); // replace with real API
    user.value   = { name: reg.value.name, email: creds.value.email };
    loggedIn.value = true;
    closeAuth();
  } catch { authErr.value = 'Registration failed. Please try again.'; }
  finally { authBusy.value = false; }
}

function logout() { loggedIn.value = false; user.value = { name: '', email: '' }; closeAuth(); }

// ── Cart helpers ─────────────────────────────────────────────────────────────
const removingId   = ref(null);
const couponInput  = ref('');
const couponError  = ref('');

const VALID_COUPONS = { NEXUS10: 10, SAVE20: 20, WELCOME5: 5 };

const freeShippingThreshold = 75;
const freeShippingProgress  = computed(() =>
  Math.min(100, (cart.getSubtotal / freeShippingThreshold) * 100)
);
const freeShippingRemaining = computed(() =>
  Math.max(0, freeShippingThreshold - cart.getSubtotal)
);
const shippingCost = computed(() => cart.getSubtotal >= freeShippingThreshold ? 0 : 9.99);
const orderTotal   = computed(() =>
  Math.max(0, cart.getSubtotal + shippingCost.value - cart.discount)
);

const THUMB_GRADIENTS = [
  'linear-gradient(135deg,#1a1208,#2e2010)',
  'linear-gradient(135deg,#0d1a1a,#102020)',
  'linear-gradient(135deg,#1a0f12,#28141a)',
  'linear-gradient(135deg,#0a1020,#101828)',
  'linear-gradient(135deg,#111006,#1e1a0e)',
  'linear-gradient(135deg,#120f18,#1c1528)',
  'linear-gradient(135deg,#0f1a10,#142014)',
  'linear-gradient(135deg,#1a1510,#26200e)',
];
function thumbGradient(id) {
  return THUMB_GRADIENTS[id % THUMB_GRADIENTS.length];
}

function removeItem(id) {
  removingId.value = id;
  setTimeout(() => { cart.removeItem(id); removingId.value = null; }, 320);
}

function applyCoupon() {
  couponError.value = '';
  const code = couponInput.value.trim().toUpperCase();
  if (!code) return;
  if (VALID_COUPONS[code]) {
    cart.applyCoupon(code, VALID_COUPONS[code]);
    couponInput.value = '';
  } else {
    couponError.value = 'Invalid coupon code. Try NEXUS10 or SAVE20.';
  }
}

function confirmClear() {
  if (window.confirm('Remove all items from your cart?')) cart.clearCart();
}

// ── Click-outside directive ──────────────────────────────────────────────────
const vClickOutside = {
  mounted(el, { value }) {
    el._out = (e) => { if (!el.contains(e.target)) value(e); };
    document.addEventListener('pointerdown', el._out);
  },
  unmounted(el) { document.removeEventListener('pointerdown', el._out); },
};
</script>

<style scoped>
/* ─── Design tokens ────────────────────────────────────────────────────────── */
.site-header {
  --h-bg:       rgba(10, 10, 10, 0.75);
  --h-bg-solid: #0a0a0a;
  --h-border:   rgba(255, 255, 255, 0.07);
  --accent:     #c9a96e;
  --accent-muted: rgba(201, 169, 110, 0.14);
  --text:       #f2ede5;
  --muted:      rgba(242, 237, 229, 0.42);
  --surface:    #111111;
  --surface-2:  #1a1a1a;
  --surface-3:  #222222;
  --panel-w:    300px;
  --r:          10px;
  --font-disp:  'Cormorant Garamond', Georgia, serif;
  --font-ui:    'DM Sans', 'Segoe UI', sans-serif;
  --shadow:     0 20px 60px rgba(0,0,0,.65), 0 4px 16px rgba(0,0,0,.4);
}

/* ─── Announce bar ────────────────────────────────────────────────────────── */
.announce-bar {
  background: var(--accent);
  color: #1a1100;
  text-align: center;
  font-family: var(--font-ui);
  font-size: .72rem;
  font-weight: 500;
  letter-spacing: .06em;
  padding: 6px 16px;
  line-height: 1;
}

/* ─── Shell ───────────────────────────────────────────────────────────────── */
.site-header {
  position: sticky;
  top: 0;
  z-index: 900;
  background: var(--h-bg);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border-bottom: 1px solid var(--h-border);
  font-family: var(--font-ui);
  transition: background .35s ease, box-shadow .35s ease;
}
.site-header.is-scrolled {
  background: var(--h-bg-solid);
  box-shadow: 0 2px 40px rgba(0,0,0,.5);
}

.header-shell {
  max-width: 1320px;
  margin: 0 auto;
  padding: 0 28px;
  height: 64px;
  display: flex;
  align-items: center;
  gap: 12px;
}

/* ─── Logo ────────────────────────────────────────────────────────────────── */
.logo {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  color: var(--accent);
  flex-shrink: 0;
  margin-right: 16px;
}
.logo-wordmark {
  font-family: var(--font-disp);
  font-size: 1.6rem;
  font-weight: 600;
  letter-spacing: .18em;
  background: linear-gradient(135deg, #f2ede5 30%, var(--accent) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  user-select: none;
}

/* ─── Nav ─────────────────────────────────────────────────────────────────── */
.primary-nav { flex: 1; display: flex; justify-content: center; }
.primary-nav ul { list-style: none; margin: 0; padding: 0; display: flex; gap: 4px; }

.nav-link {
  position: relative;
  display: block;
  padding: 8px 16px;
  color: var(--muted);
  text-decoration: none;
  font-size: .78rem;
  font-weight: 500;
  letter-spacing: .1em;
  text-transform: uppercase;
  transition: color .25s;
}
.nav-link:hover,
.router-link-active.nav-link { color: var(--text); }

.ink-bar {
  position: absolute;
  bottom: 2px; left: 16px; right: 16px;
  height: 1.5px;
  background: var(--accent);
  transform: scaleX(0);
  transform-origin: center;
  transition: transform .3s cubic-bezier(.4,0,.2,1);
  border-radius: 2px;
}
.nav-link:hover .ink-bar,
.router-link-active .ink-bar { transform: scaleX(1); }

/* ─── Action cluster ──────────────────────────────────────────────────────── */
.action-cluster {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
  gap: 10px;
}

.act-btn {
  position: relative;
  width: 40px; height: 40px;
  display: flex; align-items: center; justify-content: center;
  background: rgba(255,255,255,.07);
  border: 1px solid rgba(255,255,255,.1);
  color: rgba(242,237,229,.85);
  cursor: pointer;
  border-radius: 8px;
  transition: background .2s, color .2s, border-color .2s;
  flex-shrink: 0;
}
.act-btn:hover {
  background: rgba(255,255,255,.13);
  border-color: rgba(255,255,255,.18);
  color: #f2ede5;
}

/* Search pill */
.search-pill {
  display: flex;
  align-items: center;
  border-radius: 8px;
  overflow: hidden;
  transition: background .25s, box-shadow .25s;
}
.search-pill.expanded {
  background: var(--surface-2);
  box-shadow: 0 0 0 1px rgba(255,255,255,.08);
  padding-left: 12px;
}
.search-field {
  width: 170px;
  border: none;
  background: transparent;
  color: var(--text);
  font-family: var(--font-ui);
  font-size: .83rem;
  outline: none;
}
.search-field::placeholder { color: var(--muted); }

/* Avatar chip */
.avatar-chip {
  width: 26px; height: 26px;
  background: var(--accent);
  color: #1a1100;
  border-radius: 50%;
  font-size: .68rem;
  font-weight: 700;
  display: flex; align-items: center; justify-content: center;
  letter-spacing: .02em;
}

/* Cart badge */
.cart-trigger { position: relative; }
.badge {
  position: absolute;
  top: -5px; right: -5px;
  min-width: 17px; height: 17px;
  background: var(--accent);
  color: #1a1100;
  border-radius: 8px;
  font-size: .6rem;
  font-weight: 700;
  display: flex; align-items: center; justify-content: center;
  padding: 0 3px;
  pointer-events: none;
  animation: pop .25s cubic-bezier(.34,1.56,.64,1);
}
@keyframes pop { from { transform: scale(0); } to { transform: scale(1); } }

/* ─── Auth panel ──────────────────────────────────────────────────────────── */
.auth-node { position: relative; }

.auth-panel {
  position: absolute;
  top: calc(100% + 14px);
  right: 0;
  width: var(--panel-w);
  background: var(--surface);
  border: 1px solid rgba(255,255,255,.08);
  border-radius: var(--r);
  box-shadow: var(--shadow);
  padding: 20px;
  z-index: 999;
}
.panel-notch {
  position: absolute;
  top: -6px; right: 14px;
  width: 12px; height: 12px;
  background: var(--surface);
  border-left: 1px solid rgba(255,255,255,.08);
  border-top: 1px solid rgba(255,255,255,.08);
  transform: rotate(45deg);
}

.panel-greeting {
  font-size: .85rem;
  color: var(--muted);
  margin: 0 0 14px;
}
.panel-greeting strong { color: var(--text); }

.user-links { list-style: none; margin: 0 0 14px; padding: 0; }
.user-links li + li { border-top: 1px solid rgba(255,255,255,.05); }
.user-links a {
  display: block;
  padding: 9px 0;
  color: var(--muted);
  text-decoration: none;
  font-size: .82rem;
  letter-spacing: .04em;
  transition: color .2s;
}
.user-links a:hover { color: var(--text); }

.panel-signout {
  width: 100%;
  background: transparent;
  border: 1px solid rgba(255,255,255,.1);
  color: var(--muted);
  border-radius: 6px;
  padding: 8px;
  font-size: .78rem;
  cursor: pointer;
  transition: border-color .2s, color .2s;
}
.panel-signout:hover { border-color: var(--accent); color: var(--accent); }

/* Auth tabs */
.auth-tabs { display: flex; margin-bottom: 16px; border-bottom: 1px solid rgba(255,255,255,.07); }
.auth-tab {
  flex: 1;
  background: transparent; border: none;
  color: var(--muted);
  font-family: var(--font-ui);
  font-size: .78rem;
  font-weight: 500;
  letter-spacing: .07em;
  text-transform: uppercase;
  padding: 0 0 10px;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  transition: color .2s, border-color .2s;
}
.auth-tab.active { color: var(--accent); border-bottom-color: var(--accent); }

/* Auth form */
.auth-form { display: flex; flex-direction: column; gap: 10px; }
.input-wrap input {
  width: 100%;
  background: var(--surface-2);
  border: 1px solid rgba(255,255,255,.08);
  border-radius: 7px;
  color: var(--text);
  font-family: var(--font-ui);
  font-size: .83rem;
  padding: 10px 12px;
  outline: none;
  transition: border-color .2s;
  box-sizing: border-box;
}
.input-wrap input:focus { border-color: var(--accent); }
.input-wrap input::placeholder { color: var(--muted); }

.forgot {
  color: var(--muted);
  font-size: .72rem;
  text-align: right;
  text-decoration: none;
  margin-top: -4px;
}
.forgot:hover { color: var(--accent); }

.panel-submit {
  width: 100%;
  background: var(--accent);
  border: none;
  border-radius: 7px;
  color: #1a1100;
  font-family: var(--font-ui);
  font-size: .8rem;
  font-weight: 600;
  letter-spacing: .06em;
  text-transform: uppercase;
  padding: 11px;
  cursor: pointer;
  transition: opacity .2s, transform .15s;
  display: flex; align-items: center; justify-content: center;
  min-height: 40px;
}
.panel-submit:hover:not(:disabled) { opacity: .88; transform: translateY(-1px); }
.panel-submit:disabled { opacity: .5; cursor: not-allowed; }

.form-error {
  font-size: .75rem;
  color: #f87171;
  text-align: center;
  margin: 0;
}

/* Dot spinner */
.dot-spin {
  display: inline-block;
  width: 16px; height: 16px;
  border: 2px solid rgba(26,17,0,.3);
  border-top-color: #1a1100;
  border-radius: 50%;
  animation: spin .6s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ─── Cart drawer ──────────────────────────────────────────────────────────── */
.cart-scrim {
  position: fixed; inset: 0; z-index: 1000;
  background: rgba(0,0,0,.65);
  backdrop-filter: blur(4px);
}
.cart-drawer {
  position: absolute;
  top: 0; right: 0; bottom: 0;
  width: min(440px, 100vw);
  background: #0f0f0f;
  display: flex; flex-direction: column;
  border-left: 1px solid rgba(255,255,255,.09);
  box-shadow: -24px 0 80px rgba(0,0,0,.7);
}

/* Head */
.cd-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 18px 20px 16px;
  background: #141414;
  border-bottom: 1px solid rgba(255,255,255,.07);
  flex-shrink: 0;
}
.cd-head-left {
  display: flex; align-items: center; gap: 12px;
  color: var(--accent);
}
.cd-title {
  font-family: var(--font-disp);
  font-size: 1.35rem; font-weight: 600;
  color: var(--text); margin: 0; line-height: 1;
}
.cd-subtitle {
  font-size: .72rem; color: var(--muted);
  margin: 3px 0 0; letter-spacing: .03em;
}
.cd-close {
  width: 34px; height: 34px;
  display: flex; align-items: center; justify-content: center;
  background: rgba(255,255,255,.06);
  border: 1px solid rgba(255,255,255,.1);
  border-radius: 8px;
  color: var(--muted); cursor: pointer;
  transition: background .2s, color .2s;
}
.cd-close:hover { background: rgba(255,255,255,.12); color: var(--text); }

/* Shipping progress bar */
.cd-shipping-bar {
  padding: 10px 20px;
  background: rgba(201,169,110,.06);
  border-bottom: 1px solid rgba(201,169,110,.1);
  flex-shrink: 0;
}
.csb-text {
  font-size: .72rem; color: var(--muted);
  margin-bottom: 6px;
}
.csb-text strong { color: var(--accent); }
.csb-unlocked { color: var(--accent); font-weight: 500; }
.csb-track {
  height: 3px; background: rgba(255,255,255,.08); border-radius: 2px; overflow: hidden;
}
.csb-fill {
  height: 100%; background: linear-gradient(90deg, var(--accent), #e8c88a);
  border-radius: 2px;
  transition: width .5s cubic-bezier(.4,0,.2,1);
}

/* Empty state */
.cd-empty {
  flex: 1;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 12px; padding: 40px 24px; text-align: center;
}
.cd-empty-icon {
  width: 88px; height: 88px;
  background: rgba(255,255,255,.04);
  border: 1px solid rgba(255,255,255,.08);
  border-radius: 24px;
  display: flex; align-items: center; justify-content: center;
  color: rgba(242,237,229,.2);
  margin-bottom: 8px;
}
.cd-empty-title { font-family: var(--font-disp); font-size: 1.3rem; color: var(--text); margin: 0; }
.cd-empty-sub   { font-size: .8rem; color: var(--muted); margin: 0; }
.cd-shop-btn {
  margin-top: 8px;
  background: var(--accent); border: none; border-radius: 8px;
  color: #1a1100; font-family: var(--font-ui);
  font-size: .8rem; font-weight: 700; letter-spacing: .07em; text-transform: uppercase;
  padding: 12px 28px; cursor: pointer;
  transition: opacity .2s, transform .15s;
}
.cd-shop-btn:hover { opacity: .88; transform: translateY(-1px); }

/* Item list */
.cd-list {
  flex: 1; overflow-y: auto;
  list-style: none; margin: 0; padding: 8px 0;
}
.cd-list::-webkit-scrollbar { width: 3px; }
.cd-list::-webkit-scrollbar-track { background: transparent; }
.cd-list::-webkit-scrollbar-thumb { background: rgba(255,255,255,.08); border-radius: 2px; }

/* Individual item */
.cd-item {
  display: flex; gap: 14px;
  padding: 14px 20px;
  border-bottom: 1px solid rgba(255,255,255,.05);
  transition: background .15s, opacity .3s, transform .3s;
}
.cd-item:hover { background: rgba(255,255,255,.025); }
.cd-item.is-removing { opacity: 0; transform: translateX(24px); }

/* Thumbnail */
.cd-thumb {
  position: relative;
  width: 76px; height: 88px;
  flex-shrink: 0;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid rgba(255,255,255,.08);
}
.cd-thumb img { width: 100%; height: 100%; object-fit: cover; }
.cd-thumb-placeholder {
  width: 100%; height: 100%;
  display: flex; align-items: center; justify-content: center;
}
.cd-thumb-initial {
  font-family: var(--font-disp);
  font-size: 2rem; font-weight: 600;
  color: rgba(201,169,110,.5);
  user-select: none;
}
.cd-thumb-badge {
  position: absolute; bottom: 0; left: 0; right: 0;
  background: rgba(0,0,0,.7);
  backdrop-filter: blur(4px);
  font-size: .55rem; font-weight: 600; letter-spacing: .08em; text-transform: uppercase;
  color: var(--muted);
  text-align: center;
  padding: 3px 0;
}

/* Details column */
.cd-details { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 8px; }

.cd-details-top {
  display: flex; align-items: flex-start; justify-content: space-between; gap: 8px;
}
.cd-name-group { min-width: 0; }
.cd-item-category {
  font-size: .62rem; font-weight: 600; letter-spacing: .12em; text-transform: uppercase;
  color: var(--accent); margin: 0 0 3px;
}
.cd-item-name {
  font-size: .88rem; font-weight: 500; color: var(--text);
  margin: 0; line-height: 1.35;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

.cd-delete {
  flex-shrink: 0;
  width: 28px; height: 28px;
  display: flex; align-items: center; justify-content: center;
  background: rgba(248,113,113,.08);
  border: 1px solid rgba(248,113,113,.15);
  border-radius: 7px;
  color: rgba(248,113,113,.6);
  cursor: pointer;
  transition: background .2s, color .2s, border-color .2s;
}
.cd-delete:hover { background: rgba(248,113,113,.18); color: #f87171; border-color: rgba(248,113,113,.4); }

/* Price row */
.cd-price-row {
  display: flex; align-items: center; justify-content: space-between;
  padding: 6px 10px;
  background: rgba(255,255,255,.04);
  border: 1px solid rgba(255,255,255,.06);
  border-radius: 7px;
}
.cd-unit-price { font-size: .72rem; color: var(--muted); }
.cd-line-total  { font-size: .92rem; font-weight: 700; color: var(--accent); letter-spacing: .02em; }

/* Qty controls */
.cd-qty-wrap { display: flex; align-items: center; justify-content: space-between; }
.cd-qty-controls {
  display: flex; align-items: center;
  background: rgba(255,255,255,.05);
  border: 1px solid rgba(255,255,255,.1);
  border-radius: 8px;
  overflow: hidden;
}
.cd-qty-btn {
  width: 32px; height: 30px;
  display: flex; align-items: center; justify-content: center;
  background: transparent; border: none;
  color: var(--muted); cursor: pointer;
  transition: background .15s, color .15s;
}
.cd-qty-btn:hover { background: rgba(255,255,255,.08); color: var(--text); }
.cd-qty-btn.is-danger:hover { background: rgba(248,113,113,.12); color: #f87171; }
.cd-qty-num {
  min-width: 32px; text-align: center;
  font-size: .85rem; font-weight: 600; color: var(--text);
  border-left: 1px solid rgba(255,255,255,.08);
  border-right: 1px solid rgba(255,255,255,.08);
  padding: 0 4px; line-height: 30px;
  user-select: none;
}
.cd-qty-saving {
  font-size: .68rem; color: var(--muted); letter-spacing: .03em;
}

/* ── Footer ── */
.cd-footer {
  padding: 16px 20px 20px;
  background: #0c0c0c;
  border-top: 1px solid rgba(255,255,255,.07);
  flex-shrink: 0;
  display: flex; flex-direction: column; gap: 14px;
}

/* Coupon */
.cd-coupon {
  background: rgba(255,255,255,.03);
  border: 1px solid rgba(255,255,255,.08);
  border-radius: 10px;
  padding: 12px 14px;
  transition: border-color .2s;
}
.cd-coupon.applied { border-color: rgba(201,169,110,.3); background: rgba(201,169,110,.05); }
.cd-coupon-row { display: flex; gap: 8px; }
.cd-coupon-input {
  flex: 1;
  background: rgba(255,255,255,.06);
  border: 1px solid rgba(255,255,255,.1);
  border-radius: 7px;
  color: var(--text); font-family: var(--font-ui); font-size: .8rem;
  padding: 8px 12px; outline: none;
  transition: border-color .2s;
}
.cd-coupon-input:focus { border-color: var(--accent); }
.cd-coupon-input::placeholder { color: var(--muted); }
.cd-coupon-btn {
  background: rgba(255,255,255,.08);
  border: 1px solid rgba(255,255,255,.12);
  border-radius: 7px;
  color: var(--text); font-family: var(--font-ui);
  font-size: .75rem; font-weight: 600; letter-spacing: .06em; text-transform: uppercase;
  padding: 0 14px; cursor: pointer;
  transition: background .2s, border-color .2s;
}
.cd-coupon-btn:hover:not(:disabled) { background: rgba(255,255,255,.14); border-color: rgba(255,255,255,.2); }
.cd-coupon-btn:disabled { opacity: .4; cursor: not-allowed; }
.cd-coupon-err { font-size: .72rem; color: #f87171; margin: 8px 0 0; }
.cd-coupon-applied {
  display: flex; align-items: center; gap: 8px;
  font-size: .78rem; color: var(--accent);
}
.cd-coupon-applied strong { color: var(--text); }
.cd-coupon-applied span { flex: 1; }
.cd-coupon-remove {
  background: transparent; border: none;
  color: var(--muted); cursor: pointer; font-size: .8rem;
  padding: 2px 4px;
  transition: color .15s;
}
.cd-coupon-remove:hover { color: #f87171; }

/* Order summary */
.cd-summary {
  display: flex; flex-direction: column; gap: 6px;
  padding: 12px 14px;
  background: rgba(255,255,255,.03);
  border: 1px solid rgba(255,255,255,.07);
  border-radius: 10px;
}
.cd-summary-row {
  display: flex; justify-content: space-between; align-items: center;
  font-size: .78rem; color: var(--muted);
}
.free-label { color: #4ade80; font-weight: 600; }
.discount-row span { color: #4ade80; }
.cd-summary-total {
  display: flex; justify-content: space-between; align-items: center;
  padding-top: 8px;
  margin-top: 4px;
  border-top: 1px solid rgba(255,255,255,.08);
  font-size: .82rem; color: var(--muted);
}
.cd-summary-total strong { font-size: 1.1rem; color: var(--text); letter-spacing: .01em; }

/* Checkout CTA */
.cd-checkout {
  display: flex; align-items: center; justify-content: center; gap: 10px;
  background: var(--accent);
  color: #1a1100;
  font-family: var(--font-ui);
  font-size: .82rem; font-weight: 700; letter-spacing: .08em; text-transform: uppercase;
  text-decoration: none;
  border-radius: 10px;
  padding: 15px 20px;
  transition: opacity .2s, transform .15s, box-shadow .2s;
  box-shadow: 0 6px 28px rgba(201,169,110,.25);
}
.cd-checkout:hover { opacity: .9; transform: translateY(-1px); box-shadow: 0 10px 36px rgba(201,169,110,.35); }

/* Secondary actions */
.cd-secondary-actions {
  display: flex; gap: 8px;
}
.cd-continue, .cd-clear {
  flex: 1;
  background: transparent;
  border: 1px solid rgba(255,255,255,.1);
  border-radius: 8px;
  font-family: var(--font-ui);
  font-size: .74rem; font-weight: 500; letter-spacing: .04em;
  padding: 9px 12px; cursor: pointer;
  transition: background .2s, border-color .2s, color .2s;
}
.cd-continue { color: var(--muted); }
.cd-continue:hover { background: rgba(255,255,255,.06); color: var(--text); border-color: rgba(255,255,255,.2); }
.cd-clear { color: rgba(248,113,113,.6); border-color: rgba(248,113,113,.15); }
.cd-clear:hover { background: rgba(248,113,113,.08); color: #f87171; border-color: rgba(248,113,113,.35); }

/* Security note */
.cd-secure {
  display: flex; align-items: center; justify-content: center; gap: 6px;
  font-size: .67rem; color: rgba(242,237,229,.28); letter-spacing: .05em;
}

/* ─── Mobile ──────────────────────────────────────────────────────────────── */
.burger {
  display: none;
  flex-direction: column; gap: 5px;
  background: transparent; border: none; cursor: pointer;
  padding: 6px; border-radius: 6px;
  margin-left: auto;
}
.burger span {
  display: block; width: 22px; height: 2px;
  background: var(--muted); border-radius: 2px;
  transform-origin: center;
  transition: transform .3s, opacity .3s, background .2s;
}
.burger.open span:nth-child(1) { transform: translateY(7px) rotate(45deg); background: var(--text); }
.burger.open span:nth-child(2) { opacity: 0; }
.burger.open span:nth-child(3) { transform: translateY(-7px) rotate(-45deg); background: var(--text); }

.mobile-drawer {
  background: var(--surface);
  border-top: 1px solid var(--h-border);
  padding: 16px 24px 24px;
}
.mobile-links { list-style: none; margin: 0 0 20px; padding: 0; }
.mobile-links li { border-bottom: 1px solid rgba(255,255,255,.05); }
.mobile-links a {
  display: block;
  padding: 13px 0;
  color: var(--muted);
  text-decoration: none;
  font-size: .88rem;
  letter-spacing: .06em;
  text-transform: uppercase;
  transition: color .2s, padding-left .2s;
}
.mobile-links a:hover { color: var(--text); padding-left: 6px; }

.mobile-auth-row { display: flex; gap: 10px; align-items: center; }
.mob-btn {
  flex: 1;
  background: var(--accent);
  border: none;
  border-radius: 7px;
  color: #1a1100;
  font-family: var(--font-ui);
  font-size: .78rem;
  font-weight: 600;
  letter-spacing: .06em;
  text-transform: uppercase;
  text-decoration: none;
  text-align: center;
  padding: 10px;
  cursor: pointer;
  display: block;
}
.mob-btn.outline {
  background: transparent;
  border: 1px solid rgba(255,255,255,.12);
  color: var(--muted);
}
.mob-user { color: var(--muted); font-size: .8rem; flex: 1; }

/* ─── Transitions ─────────────────────────────────────────────────────────── */
.panel-drop-enter-active,
.panel-drop-leave-active { transition: opacity .2s, transform .2s; }
.panel-drop-enter-from,
.panel-drop-leave-to { opacity: 0; transform: translateY(-8px); }

.fade-slide-enter-active,
.fade-slide-leave-active { transition: opacity .2s, width .3s; }
.fade-slide-enter-from,
.fade-slide-leave-to { opacity: 0; width: 0 !important; }

.mobile-slide-enter-active,
.mobile-slide-leave-active { transition: opacity .25s, transform .25s; }
.mobile-slide-enter-from,
.mobile-slide-leave-to { opacity: 0; transform: translateY(-10px); }

.cart-slide-enter-active,
.cart-slide-leave-active { transition: opacity .3s; }
.cart-slide-enter-active .cart-drawer,
.cart-slide-leave-active .cart-drawer { transition: transform .35s cubic-bezier(.4,0,.2,1); }
.cart-slide-enter-from,
.cart-slide-leave-to { opacity: 0; }
.cart-slide-enter-from .cart-drawer,
.cart-slide-leave-to .cart-drawer { transform: translateX(100%); }

/* ─── Responsive ──────────────────────────────────────────────────────────── */
@media (max-width: 860px) {
  .primary-nav, .announce-bar { display: none; }
  .burger { display: flex; }
  .logo { margin-right: 0; }
}
@media (max-width: 480px) {
  .header-shell { padding: 0 16px; }
  .search-field { width: 130px; }
}

.search-submit-btn {
  width: 30px; height: 30px;
  display: flex; align-items: center; justify-content: center;
  background: var(--accent);
  border: none;
  border-radius: 6px;
  color: #1a1100;
  cursor: pointer;
  flex-shrink: 0;
  margin-right: 4px;
  transition: opacity .2s;
}
.search-submit-btn:hover { opacity: .85; }
</style>